import { cloneElement, forwardRef, isValidElement, useMemo } from 'react'
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown'
import { IconButton, Stack } from '@mui/material'
import PropTypes from 'prop-types'
import { useCollapsedState } from '~/hooks'
import Subtitle from './Subtitle'

const Section = forwardRef((props, ref) => {
  const {
    as: Component = 'section',
    children,
    sectionId,
    title,
    ...rest
  } = props

  const { collapsed, toggleCollapsed } = useCollapsedState(
    'sections',
    sectionId
  )

  const simple = useMemo(() => typeof title === 'string', [title])

  const titleNode = useMemo(() => {
    if (!isValidElement(title)) {
      return title
    }

    return cloneElement(title, { collapsed })
  }, [collapsed, title])

  const button = (
    <IconButton
      sx={{
        color: 'inherit',
        transform: `scaleY(${collapsed ? 1 : -1})`,
      }}
      aria-label="close"
      size="small"
      onClick={simple ? undefined : () => toggleCollapsed()}
    >
      <ExpandCircleDownIcon />
    </IconButton>
  )

  return (
    <Component ref={ref} {...rest}>
      {title &&
        (simple ? (
          <Subtitle>
            <Stack
              direction="row"
              spacing={0.5}
              as="span"
              sx={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
              }}
              onClick={() => toggleCollapsed()}
            >
              {button}
              {title}
            </Stack>
          </Subtitle>
        ) : (
          <Subtitle
            sx={{
              color: 'primary.default',
              display: 'flex',
              alignItems: 'center',
              fontWeight: '700',
            }}
            variant="div"
          >
            {button}
            {titleNode}
          </Subtitle>
        ))}
      {!collapsed && children}
    </Component>
  )
})

Section.displayName = 'Section'

Section.propTypes = {
  as: PropTypes.elementType,
  sectionId: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
}

export default Section
